/**
 * outreachFeatures.js
 *
 * Einzige Quelle der Wahrheit für die Outreach-Job-Lizenzen.
 *
 * Zwei Ebenen, analog zu hasAcademy + academyTiers:
 *   hasOutreachJobs  — mindestens ein Modus ist an (folgt aus outreachModes)
 *   outreachModes    — Automatic Jobs (send) und/oder Automatic Prepare
 *
 * Erlaubte Kombinationen: beide, nur Prepare, nur Auto Jobs, oder keines.
 *
 * Beide liegen beim Owner der aktiven World unter Users/{activeWorldId}/…,
 * nie beim eingeloggten User. Team-Mitarbeiter erben die Freischaltung ihres
 * Owners dadurch automatisch (siehe useOutreachAccess). Gesetzt wird
 * ausschliesslich im Team-Dashboard (PremiumToggle).
 *
 * Die Modi entsprechen 1:1 dem deliveryMode eines Jobs, damit Lizenz und
 * Job-Konfiguration nie auseinanderlaufen.
 *
 * ACHTUNG: Der Resolver ist für die Cloud Functions in
 * functions/helpers/outreachModes.js gespiegelt — Änderungen hier gehören
 * auch dorthin, sonst weicht die serverseitige Sperre von der UI ab.
 */

// tone: Farbvariante der Chips im Team-Dashboard (premium-toggle-chip-<tone>).
// Zwei unabhängige Modi. Erlaubt: beide, nur Prepare, nur Auto Jobs, oder keines.
export const OUTREACH_MODES = [
  {
    id: 'send',
    key: 'send',
    name: 'Automatic Jobs',
    short: 'Auto Jobs',
    icon: 'fa-rocket',
    tone: 'teal',
    description: 'Jobs schreiben und versenden die Mails selbst.',
  },
  {
    id: 'prepare',
    key: 'prepare',
    name: 'Automatic Prepare',
    short: 'Prepare',
    icon: 'fa-pen-to-square',
    tone: 'blue',
    description: 'Jobs schreiben die Mails, der Versand bleibt manuell.',
  },
];

export const OUTREACH_MODE_IDS = OUTREACH_MODES.map((m) => m.id);

export const OUTREACH_ALL_LOCKED = { send: false, prepare: false };

/**
 * Normalisiert den Rohwert von Users/{ownerId}/outreachModes.
 *
 * Fehlt der Knoten komplett, war der Account freigeschaltet, bevor es die
 * Modi gab — damals konnte ein Job ausschliesslich automatisch versenden.
 * Genau das geben wir zurück, damit bestehende Kunden durch die Einführung
 * der Modi nichts verlieren und "Prepare only" bewusst verkauft werden muss.
 * Sobald das Dashboard den Knoten einmal geschrieben hat, zählt nur noch er.
 */
export function resolveOutreachModes(raw) {
  if (raw && typeof raw === 'object' && OUTREACH_MODE_IDS.some((id) => raw[id] !== undefined)) {
    return {
      send: raw.send === true,
      prepare: raw.prepare === true,
    };
  }
  return { send: true, prepare: false };
}

/**
 * Effektive Modi für die UI: ohne Master-Flag ist nichts an — auch dann nicht,
 * wenn der Resolver für einen fehlenden Knoten noch den Legacy-Default
 * (nur Auto Jobs) zurückgibt. Sonst lässt sich "nur Prepare" nicht setzen,
 * weil ein leerer Knoten stillschweigend send=true mitbringt.
 */
export function effectiveOutreachModes(enabled, raw) {
  if (!enabled) return { ...OUTREACH_ALL_LOCKED };
  return resolveOutreachModes(raw);
}

/**
 * Ist dieser deliveryMode für den Account freigeschaltet?
 * Ein Job ohne deliveryMode ist ein Alt-Job und zählt als 'send'.
 */
export function isOutreachModeAllowed(modes, deliveryMode) {
  return (deliveryMode === 'prepare' ? modes.prepare : modes.send) === true;
}
